import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let lastQuotaToast = 0;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const api = axios.create({ baseURL: API });

let _token = localStorage.getItem('sdr_token');

export function setAuthToken(token) {
  _token = token;
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
}

// Init token on load
if (_token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${_token}`;
}

// Append the global LA scope to every GET so all pilot dashboards are LA-aware.
api.interceptors.request.use(config => {
  const la = localStorage.getItem('adoptic_la_scope');
  const urlHasLa = (config.url || '').includes('la=');
  if (la && la !== 'tvca' && !urlHasLa && !(config.params && config.params.la)) {
    config.params = { ...(config.params || {}), la };
  }
  return config;
});

api.interceptors.response.use(
  res => res,
  async error => {
    const d = error.response?.data?.detail;
    if (error.response?.status === 429 && d?.code === 'DAILY_QUERY_LIMIT') {
      if (Date.now() - lastQuotaToast > 8000) {
        lastQuotaToast = Date.now();
        toast.error(`Daily query limit reached (${d.used}/${d.cap})`, {
          description: d.message,
          action: { label: 'Upgrade', onClick: () => window.location.assign('/pricing') },
          duration: 8000,
        });
      }
      window.dispatchEvent(new CustomEvent('adoptic:quota', { detail: d }));
    }
    if (error.response?.status === 429 && d?.code === 'RATE_LIMITED') {
      toast.error('Too many requests — please wait', {
        id: 'rate-limited',
        description: `Limit is ${d.limit}/min. Try again in ${d.retry_after}s.`,
        duration: Math.min(d.retry_after || 5, 15) * 1000,
      });
    }
    if (error.response?.status === 401 && d?.code === 'SESSION_IDLE') {
      localStorage.removeItem('sdr_token');
      localStorage.removeItem('sdr_refresh_token');
      setAuthToken(null);
      if (window.location.pathname !== '/login') window.location.replace('/login?reason=idle');
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;
      try {
        const refreshToken = localStorage.getItem('sdr_refresh_token');
        const { data } = await axios.post(`${API}/auth/refresh`, {}, {
          headers: refreshToken ? { 'Authorization': `Bearer ${refreshToken}` } : {}
        });
        if (data.access_token) {
          localStorage.setItem('sdr_token', data.access_token);
          setAuthToken(data.access_token);
          error.config.headers['Authorization'] = `Bearer ${data.access_token}`;
          if (data.refresh_token) {
            localStorage.setItem('sdr_refresh_token', data.refresh_token);
          }
          return api(error.config);
        }
      } catch (refreshErr) {
        localStorage.removeItem('sdr_token');
        localStorage.removeItem('sdr_refresh_token');
        setAuthToken(null);
        const idle = refreshErr?.response?.data?.detail?.code === 'SESSION_IDLE';
        if (window.location.pathname !== '/login' && window.location.pathname !== '/public') {
          window.location.replace(idle ? '/login?reason=idle' : '/login');
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
